import { useState, useEffect, useCallback } from 'react';
import { useSearchImporterOrders } from '../../../../api/services/lupp/order/importer/importer';
import type { ImporterOrder } from '../../../../api/services/lupp/order/importer/dto.importer';

/**
 * Backward-compatible empty stub — consumed by useAllPendingOrders while that hook is
 * still stub-based. Remove once useAllPendingOrders is wired to the real API.
 */
export const STUB_IMPORTER_ORDERS: ImporterOrder[] = [];

type UseImporterOrdersParams = {
  enabled?: boolean;
};

/**
 * Maps filter card values to the API status filter arrays.
 * Card values match API statusName exactly; mapping kept explicit for clarity and future changes.
 */
const CARD_VALUE_TO_API_STATUSES: Record<string, string[]> = {
  INITIATED: ['INITIATED'],
  PARTIALLY_FULFILLED: ['PARTIALLY_FULFILLED'],
  FULFILLED: ['FULFILLED'],
  CANCELED: ['CANCELED'],
};

/**
 * Hook for fetching importer orders from the x-importer API.
 * Manages pagination, debounced search, sort, and status filter state,
 * delegating data fetching to useSearchImporterOrders.
 */
export const useImporterOrders = ({ enabled = true }: UseImporterOrdersParams = {}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const apiResult = useSearchImporterOrders({
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch || undefined,
    sortField,
    sortOrder,
    statuses: statusFilter
      ? (CARD_VALUE_TO_API_STATUSES[statusFilter] ?? [statusFilter])
      : undefined,
    enabled,
  });

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortOrder(direction);
    setCurrentPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((value: string | null) => {
    setStatusFilter(value);
    setCurrentPage(0);
  }, []);

  return {
    data: apiResult.data?.items ?? [],
    totalCount: apiResult.data?.totalCount ?? 0,
    statusGroupCounts: apiResult.data?.statusGroupCounts ?? {},
    isLoading: apiResult.isLoading,
    isFetching: apiResult.isFetching,
    error: apiResult.error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSearchChange: handleSearchChange,
    onSort: handleSort,
    onStatusFilterChange: handleStatusFilterChange,
  };
};
